import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const QuickViewModal = ({ product, isOpen, onClose, onAddToCart, onAddToWishlist, userTier }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  if (!isOpen || !product) return null;

  const getTierDiscount = () => {
    const tierDiscounts = {
      'Intro': 5,
      'All Access': 10,
      'Flex': 15,
      'Premium': 20
    };
    return tierDiscounts?.[userTier] || 0;
  };

  const getDiscountedPrice = () => {
    const discount = getTierDiscount();
    return product?.price * (1 - discount / 100);
  };

  const handleAddToCart = () => {
    onAddToCart?.({ ...product, quantity });
    onClose();
  };

  const renderRating = () => {
    return (
      <div className="flex items-center space-x-2">
        <div className="flex items-center space-x-1">
          {[...Array(5)]?.map((_, i) => (
            <Icon
              key={i}
              name="Star"
              size={16}
              className={i < Math.floor(product?.rating) ? 'text-accent fill-current' : 'text-muted'}
            />
          ))}
        </div>
        <span className="text-sm text-muted-foreground">
          {product?.rating} ({product?.reviewCount} reviews)
        </span>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-wellness-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
          <h2 className="font-heading font-semibold text-lg text-foreground">Quick View</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Product Images */}
            <div className="space-y-4">
              <div className="relative aspect-square bg-muted rounded-lg overflow-hidden">
                <Image
                  src={product?.images?.[selectedImage] || product?.image}
                  alt={product?.name}
                  className="w-full h-full object-cover"
                />
                
                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-col space-y-2">
                  {product?.isFeatured && (
                    <span className="bg-accent text-accent-foreground px-3 py-1 rounded-full text-sm font-medium">
                      Featured
                    </span>
                  )}
                  {product?.isNew && (
                    <span className="bg-success text-success-foreground px-3 py-1 rounded-full text-sm font-medium">
                      New
                    </span>
                  )}
                  {getTierDiscount() > 0 && (
                    <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                      {getTierDiscount()}% Off
                    </span>
                  )}
                </div>

                {/* Wishlist Button */}
                <button
                  onClick={() => onAddToWishlist?.(product?.id)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-wellness"
                >
                  <Icon name="Heart" size={20} className="text-muted-foreground" />
                </button>
              </div>

              {/* Image Thumbnails */}
              {product?.images && product?.images?.length > 1 && (
                <div className="flex space-x-2 overflow-x-auto">
                  {product?.images?.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-wellness ${
                        selectedImage === index ? 'border-primary' : 'border-transparent'
                      }`}
                    >
                      <Image
                        src={image}
                        alt={`${product?.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Details */}
            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-sm text-primary font-medium bg-primary/10 px-2 py-1 rounded-full">
                    {product?.category}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    by {product?.centerName}
                  </span>
                </div>
                
                <h1 className="font-heading font-bold text-2xl text-foreground mb-2">
                  {product?.name}
                </h1>
                
                {renderRating()}
              </div>

              {/* Description */}
              <div>
                <p className="text-muted-foreground leading-relaxed">
                  {product?.description}
                </p>
              </div>

              {/* Features */}
              {product?.features && (
                <div>
                  <h3 className="font-heading font-semibold text-foreground mb-3">Key Features</h3>
                  <ul className="space-y-2">
                    {product?.features?.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Pricing */}
              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    {getTierDiscount() > 0 ? (
                      <>
                        <span className="text-2xl font-bold text-foreground">
                          ${getDiscountedPrice()?.toFixed(2)}
                        </span>
                        <span className="text-lg text-muted-foreground line-through">
                          ${product?.price?.toFixed(2)}
                        </span>
                      </>
                    ) : (
                      <span className="text-2xl font-bold text-foreground">
                        ${product?.price?.toFixed(2)}
                      </span>
                    )}
                  </div>
                  
                  {product?.originalPrice && product?.originalPrice > product?.price && (
                    <span className="text-sm text-success font-medium">
                      Save ${(product?.originalPrice - product?.price)?.toFixed(2)}
                    </span>
                  )}
                </div>

                {/* Tier Benefits */}
                {userTier && getTierDiscount() > 0 && (
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="Award" size={16} className="text-primary" />
                    <span className="text-sm text-primary font-medium">
                      {userTier} Member: {getTierDiscount()}% discount applied
                    </span>
                  </div>
                )}

                {/* Availability */}
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    product?.availability === 'available' ? 'bg-success' : 
                    product?.availability === 'limited' ? 'bg-warning' : 'bg-error'
                  }`}></div>
                  <span className="text-sm text-muted-foreground">
                    {product?.availability === 'available' ? 'In Stock' :
                     product?.availability === 'limited' ? 'Limited Stock' : 'Out of Stock'}
                  </span>
                </div>
              </div>

              {/* Quantity & Actions */}
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <label className="text-sm font-medium text-foreground">Quantity:</label>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      <Icon name="Minus" size={16} />
                    </Button>
                    <span className="w-12 text-center font-medium">{quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(quantity + 1)}
                    >
                      <Icon name="Plus" size={16} />
                    </Button>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Button
                    variant="default"
                    onClick={handleAddToCart}
                    disabled={product?.availability === 'unavailable'}
                    className="flex-1"
                    iconName="ShoppingCart"
                    iconPosition="left"
                  >
                    Add to Cart
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => onAddToWishlist?.(product?.id)}
                    iconName="Heart"
                  />
                </div>
              </div>

              {/* Center Info */}
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="MapPin" size={20} color="white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground">{product?.centerName}</h4>
                    <p className="text-sm text-muted-foreground">{product?.centerLocation}</p>
                    <div className="flex items-center space-x-1 mt-1">
                      {[...Array(5)]?.map((_, i) => (
                        <Icon
                          key={i}
                          name="Star"
                          size={12}
                          className={i < Math.floor(product?.centerRating || 4.5) ? 'text-accent fill-current' : 'text-muted'}
                        />
                      ))}
                      <span className="text-xs text-muted-foreground ml-1">
                        ({product?.centerReviews || 150} reviews)
                      </span>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    Visit Center
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickViewModal;